import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    Brand as BrandType,
    CupType,
    TasteType,
    LanguageList as LanguageListType,
    AutomatModel as ModelType,
    AutomatFillinsType,
    AutomatType,
    ProductType,
    Outlet as OutletType,
    BaseDosagesType,
    GoodsItem,
} from '../../types'
import { Brands as BrandsService } from '../../services/Brands'
import { Localizations as LocalizationsService } from '../../services/Localizations'
import { RootState } from '../index'
import { CupsService } from '../../services/Cups'
import { TasteService } from '../../services/Tastes'
import { FillingsService } from '../../services/Fillings'
import { Models } from '../../services/Models'
import { Automats as AutomatsService } from '../../services/Automats'
import { ProductsService } from '../../services/Products'
import { Outlets as OutletsService } from '../../services/Outlets'
import { Goods as GoodsService } from '../../services/Goods'
import { action } from '@storybook/addon-actions'
import {
    knowledgeBaseSendData,
    KnowledgeBaseService,
} from '../../services/KnowledgeBase'

export const initialState = {
    brands: [] as Array<BrandType>,
    tastes: [] as Array<TasteType>,
    cups: [] as Array<CupType>,
    models: [] as Array<ModelType>,
    langs: [] as Array<LanguageListType>,
    fillings: [] as Array<AutomatFillinsType>,
    automats: [] as Array<AutomatType>,
    products: [] as Array<ProductType>,
    outlets: [] as Array<OutletType>,
    baseDosages: [] as Array<BaseDosagesType>,
    goods: [] as Array<GoodsItem>,
    KnowledgeBase: {} as knowledgeBaseSendData,
}

export const loadBrands = createAsyncThunk('storage/loadBrands', async () => {
    const limit = 100
    let offset = 0

    let brands: Array<BrandType> = []

    while (true) {
        const result = await BrandsService.getList({ offset, limit })

        if (!result || result.length === 0) {
            break
        }

        brands = [...brands, ...result]
        offset += limit
    }

    return brands
})

export const loadTastes = createAsyncThunk('storage/loadTastes', async () => {
    const limit = 100
    let offset = 0

    let tastesArr: Array<TasteType> = []

    while (true) {
        const tastes = await TasteService.getTastes({ limit, offset })

        if (!tastes || tastes.length === 0) {
            break
        }

        tastesArr = [...tastesArr, ...tastes]
        offset += limit
    }
    return tastesArr
})
export const loadFillings = createAsyncThunk(
    'storage/loadFillings',
    async () => {
        const limit = 100
        let offset = 0

        let data: Array<AutomatFillinsType> = []

        while (true) {
            const tastes = await FillingsService.getFillings({ limit, offset })

            if (!tastes || tastes.length === 0) {
                break
            }

            data = [...data, ...tastes]
            offset += limit
        }
        return data
    }
)
export const loadModels = createAsyncThunk('storage/loadModels', async () => {
    const limit = 100
    let offset = 0

    let data: Array<ModelType> = []

    while (true) {
        const tastes = await Models.getModels({ limit, offset })

        if (!tastes || tastes.length === 0) {
            break
        }

        data = [...data, ...tastes]
        offset += limit
    }
    return data
})

export const loadAutomats = createAsyncThunk(
    'storage/loadAutomats',
    async () => {
        const limit = 100
        let offset = 0

        let data: Array<AutomatType> = []

        while (true) {
            const automats = await AutomatsService.getLists({ limit, offset })

            if (!automats || automats.length === 0) {
                break
            }

            data = [...data, ...automats]
            offset += limit
        }

        return data
    }
)

export const loadProducts = createAsyncThunk(
    'storage/loadProducts',
    async () => {
        const limit = 100
        let offset = 0

        let data: Array<ProductType> = []

        while (true) {
            const products = await ProductsService.getProductList({
                limit,
                offset,
            })

            if (!products || products.length === 0) {
                break
            }

            data = [...data, ...products]
            offset += limit
        }

        return data
    }
)
export const loadOutlets = createAsyncThunk('storage/loadOutlets', async () => {
    const limit = 100
    let offset = 0

    let data: Array<OutletType> = []

    while (true) {
        const outlets = await OutletsService.getList({ limit, offset })

        if (!outlets || outlets.length === 0) {
            break
        }

        data = [...data, ...outlets]
        offset += limit
    }

    return data
})

export const loadGoods = createAsyncThunk('storage/loadGoods', async () => {
    const limit = 100
    let offset = 0

    let data: Array<GoodsItem> = []

    while (true) {
        const goods = await GoodsService.getList({ limit, offset })

        if (!goods || goods.length === 0) {
            break
        }

        data = [...data, ...goods]
        offset += limit
    }

    return data
})

export const fetchBaseDosages = createAsyncThunk(
    'storage/fetchBaseDosages',
    async () => {
        const dosages = await TasteService.fetchBaseDosages()
        return dosages
    }
)

export const loadCups = createAsyncThunk('storage/loadCups', async () => {
    const cups = await CupsService.getCups()
    return cups
})

export const loadLangs = createAsyncThunk('storage/loadLangs', async () => {
    const langs = await LocalizationsService.getLanguages()
    return langs
})

export const loadKnowledgeBase = createAsyncThunk(
    'storage/loadKnowledgeBase',
    async () => {
        const response = await KnowledgeBaseService.getKnowledgeBase()
        return response
    }
)

export const storageSlice = createSlice({
    name: 'storage',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(loadKnowledgeBase.fulfilled, (state, action) => {
            state.KnowledgeBase.data = action.payload
        })

        builder.addCase(loadBrands.fulfilled, (state, action) => {
            state.brands = action.payload
        })

        builder.addCase(loadTastes.fulfilled, (state, action) => {
            state.tastes = action.payload
        })

        builder.addCase(loadCups.fulfilled, (state, action) => {
            state.cups = action.payload
        })

        builder.addCase(loadModels.fulfilled, (state, action) => {
            state.models = action.payload
        })

        builder.addCase(loadLangs.fulfilled, (state, action) => {
            state.langs = action.payload
        })
        builder.addCase(loadFillings.fulfilled, (state, action) => {
            state.fillings = action.payload
        })
        builder.addCase(loadAutomats.fulfilled, (state, action) => {
            state.automats = action.payload
        })
        builder.addCase(loadProducts.fulfilled, (state, action) => {
            state.products = action.payload
        })

        builder.addCase(loadOutlets.fulfilled, (state, action) => {
            state.outlets = action.payload
        })

        builder.addCase(loadGoods.fulfilled, (state, action) => {
            state.goods = action.payload
        })

        builder.addCase(fetchBaseDosages.fulfilled, (state, action) => {
            state.baseDosages = action.payload
        })
    },
})

export const selectBrands = (state: RootState) => state.storage.brands
export const selectTastes = (state: RootState) => state.storage.tastes
export const selectCups = (state: RootState) => state.storage.cups
export const selectModels = (state: RootState) => state.storage.models
export const selectLangs = (state: RootState) => state.storage.langs
export const selectFillings = (state: RootState) => state.storage.fillings
export const selectAutomats = (state: RootState) => state.storage.automats
export const selectProducts = (state: RootState) => state.storage.products
export const selectOutlets = (state: RootState) => state.storage.outlets
export const selectBaseDosages = (state: RootState) => state.storage.baseDosages
export const selectGoods = (state: RootState) => state.storage.goods
export const selectKnowledgeBase = (state: RootState) =>
    state.storage.KnowledgeBase

export default storageSlice.reducer
